import { useEffect, useMemo, useState } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY
  };
}

async function loadSnapshot(submissionId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?id=eq.${encodeURIComponent(submissionId)}&select=id,structured_form_snapshot,submitted_at&limit=1`,
    { headers: headers() }
  );
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`Loading submitted Structured Form failed (${response.status}): ${text || response.statusText}`);
  }
  const rows = text ? JSON.parse(text) : [];
  return rows?.[0] || null;
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function answerEmpty(field) {
  if (field?.field_type === 'multiple_choice') {
    return !Array.isArray(field.response_json) || field.response_json.length === 0;
  }
  return !stripHtml(field?.response_text);
}

function Answer({ field }) {
  if (answerEmpty(field)) {
    return (
      <div style={styles.empty}>
        No response.
      </div>
    );
  }

  if (
    field.field_type ===
    'long_text'
  ) {
    return (
      <div
        style={styles.rich}
        dangerouslySetInnerHTML={{
          __html:
            field.response_text ||
            ''
        }}
      />
    );
  }

  if (
    field.field_type ===
    'multiple_choice'
  ) {
    const raw =
      Array.isArray(
        field.response_json
      )
        ? field.response_json.map(
            item =>
              String(item)
          )
        : [];

    const prefix =
      '__HSS_OTHER_TEXT__:';

    const otherDetail =
      raw.find(item =>
        item.startsWith(
          prefix
        )
      );

    const detail =
      otherDetail
        ? otherDetail.slice(
            prefix.length
          )
        : '';

    const values =
      raw.filter(item =>
        !item.startsWith(
          prefix
        )
      );

    return (
      <div style={styles.choices}>
        {values.map(option => (
          <span
            key={String(option)}
            style={styles.choice}
          >
            {
              String(option)
                .trim()
                .toLowerCase() ===
                'other' &&
              detail
                ? `Other — ${detail}`
                : String(option)
            }
          </span>
        ))}
      </div>
    );
  }

  return (
    <div style={styles.plain}>
      {field.response_text || ''}
    </div>
  );
}

function matchesField(comment, field) {
  return (
    comment?.anchor_data?.kind === 'structured_form_field' &&
    String(comment.anchor_data.field_id || '') === String(field.id || '')
  );
}

function anchorFor(section, field) {
  return {
    kind: 'structured_form_field',
    version: 1,
    section_id: section.id,
    section_title: section.title || '',
    section_order: section.section_order ?? null,
    field_id: field.id,
    field_label: field.label || '',
    field_prompt: field.prompt || '',
    field_order: field.field_order ?? null
  };
}

export default function StructuredFormPeerReviewViewer({
  targetSubmissionId,
  comments = [],
  editable = false,
  onAddComment,
  onUpdateComment,
  onDeleteComment
}) {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingFieldId, setAddingFieldId] = useState(null);
  const [draft, setDraft] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');

    loadSnapshot(targetSubmissionId)
      .then(row => {
        if (!cancelled) setSubmission(row);
      })
      .catch(err => {
        if (!cancelled) setError(err?.message || 'Could not load the submitted Structured Form.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [targetSubmissionId]);

  const snapshot = submission?.structured_form_snapshot || null;

  const sections = useMemo(
    () =>
      (Array.isArray(snapshot?.sections) ? snapshot.sections : [])
        .slice()
        .sort(
          (a, b) =>
            Number(a.section_order || 0) -
              Number(b.section_order || 0) ||
            String(a.id || '').localeCompare(String(b.id || ''))
        ),
    [snapshot]
  );

  async function addComment(section, field) {
    const text = draft.trim();
    if (!text || !onAddComment) return;

    try {
      await onAddComment({
        pageNumber: 1,
        selectedText:
          String(field.label || field.prompt || 'Structured Form answer').trim() ||
          'Structured Form answer',
        anchorData: anchorFor(section, field),
        commentText: text
      });
      setDraft('');
      setAddingFieldId(null);
    } catch (err) {
      setError(err?.message || 'Could not add this comment.');
    }
  }

  async function editComment(comment) {
    if (!editable || !onUpdateComment) return;
    const next = window.prompt('Edit comment', comment.comment_text || '');
    if (next === null || !next.trim()) return;

    setBusyId(comment.id);
    try {
      await onUpdateComment(comment.id, next.trim());
    } catch (err) {
      setError(err?.message || 'Could not update this comment.');
    } finally {
      setBusyId(null);
    }
  }

  async function deleteComment(comment) {
    if (!editable || !onDeleteComment) return;
    if (!window.confirm('Delete this Peer Review comment?')) return;

    setBusyId(comment.id);
    try {
      await onDeleteComment(comment.id);
    } catch (err) {
      setError(err?.message || 'Could not delete this comment.');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <div style={styles.state}>Loading submitted Structured Form...</div>;
  }

  if (!snapshot) {
    return (
      <div style={styles.error}>
        {error || 'This allocation does not contain a reviewable Structured Form snapshot.'}
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.banner}>
        <strong>
          Submitted assignment · Instance {snapshot?.instance?.instance_order || '?'}
        </strong>
        <span>Read only</span>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {sections.map((section, sectionIndex) => (
        <section key={section.id} style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>Section {sectionIndex + 1}</div>
            <div style={styles.sectionTitleRow}>
              <h3 style={styles.sectionTitle}>
                {section.title || `Section ${sectionIndex + 1}`}
              </h3>
              <span style={section.active ? styles.activePill : styles.inactivePill}>
                {section.active
                  ? 'Included in this instance'
                  : 'Not open in this instance'}
              </span>
            </div>
            {section.description && (
              <div style={styles.sectionDescription}>{section.description}</div>
            )}
          </div>

          {(section.fields || [])
            .slice()
            .sort(
              (a, b) =>
                Number(a.field_order || 0) -
                  Number(b.field_order || 0) ||
                String(a.id || '').localeCompare(String(b.id || ''))
            )
            .map((field, fieldIndex) => {
              const fieldComments = comments.filter(comment =>
                matchesField(comment, field)
              );
              const adding =
                String(addingFieldId || '') === String(field.id || '');

              return (
                <div key={field.id} style={styles.field}>
                  <div style={styles.fieldHeader}>
                    <div>
                      <div style={styles.fieldLabel}>
                        {fieldIndex + 1}. {field.label || 'Question'}
                        {field.required && (
                          <span style={styles.required}>Required</span>
                        )}
                      </div>
                      {field.prompt && (
                        <div style={styles.prompt}>{field.prompt}</div>
                      )}
                    </div>

                    {editable && (
                      <button
                        type="button"
                        style={styles.commentButton}
                        onClick={() => {
                          setAddingFieldId(adding ? null : field.id);
                          setDraft('');
                        }}
                      >
                        {adding ? 'Cancel' : 'Comment on answer'}
                      </button>
                    )}
                  </div>

                  <div style={styles.answer}>
                    <Answer field={field} />
                  </div>

                  {fieldComments.map(comment => (
                    <div key={comment.id} style={styles.comment}>
                      <div style={styles.commentText}>{comment.comment_text}</div>
                      {editable && (
                        <div style={styles.commentActions}>
                          <button
                            type="button"
                            style={styles.textButton}
                            disabled={busyId === comment.id}
                            onClick={() => editComment(comment)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            style={styles.deleteButton}
                            disabled={busyId === comment.id}
                            onClick={() => deleteComment(comment)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {editable && adding && (
                    <div style={styles.addBox}>
                      <textarea
                        rows={4}
                        style={styles.textarea}
                        value={draft}
                        onChange={event => setDraft(event.target.value)}
                        placeholder="Write a comment on this answer..."
                      />
                      <button
                        type="button"
                        style={styles.saveButton}
                        disabled={!draft.trim()}
                        onClick={() => addComment(section, field)}
                      >
                        Add comment
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
        </section>
      ))}
    </div>
  );
}

const styles = {
  wrap: { display: 'grid', gap: '14px', marginTop: '10px' },
  banner: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '10px 12px',
    border: '1px solid #cbd5df',
    borderRadius: '8px',
    background: '#f7fafc',
    color: '#334e68',
    fontSize: '12px'
  },
  state: {
    padding: '14px',
    border: '1px solid #dce3e8',
    borderRadius: '8px',
    color: '#66788a'
  },
  error: {
    padding: '8px 10px',
    border: '1px solid #efb7b7',
    borderRadius: '7px',
    background: '#fff4f4',
    color: '#912d2d',
    fontSize: '12px'
  },
  section: {
    border: '1px solid #d9e2e8',
    borderRadius: '9px',
    overflow: 'hidden',
    background: '#fff'
  },
  sectionHeader: {
    padding: '12px 14px',
    borderBottom: '1px solid #e4e9ed',
    background: '#f8fafb'
  },
  sectionNumber: {
    fontSize: '10px',
    fontWeight: 800,
    textTransform: 'uppercase',
    color: '#829ab1'
  },
  sectionTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    marginTop: '3px'
  },
  sectionTitle: { margin: 0, color: '#243b53', fontSize: '16px' },
  sectionDescription: {
    marginTop: '5px',
    color: '#627d98',
    fontSize: '12px',
    lineHeight: 1.45
  },
  activePill: {
    borderRadius: '999px',
    padding: '3px 7px',
    background: '#e7f6ed',
    color: '#276749',
    fontSize: '9px',
    fontWeight: 800
  },
  inactivePill: {
    borderRadius: '999px',
    padding: '3px 7px',
    background: '#edf1f4',
    color: '#6b7c8a',
    fontSize: '9px',
    fontWeight: 800
  },
  field: {
    padding: '13px 14px',
    borderTop: '1px solid #edf1f4'
  },
  fieldHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '10px'
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: 800,
    color: '#334e68'
  },
  required: {
    marginLeft: '7px',
    color: '#9a3412',
    fontSize: '9px',
    fontWeight: 800,
    textTransform: 'uppercase'
  },
  prompt: {
    marginTop: '4px',
    color: '#66788a',
    fontSize: '12px',
    lineHeight: 1.45
  },
  answer: {
    marginTop: '9px',
    padding: '10px 11px',
    border: '1px solid #e0e6ea',
    borderRadius: '7px',
    background: '#fbfcfd'
  },
  rich: { color: '#243b53', fontSize: '13px', lineHeight: 1.55 },
  plain: {
    whiteSpace: 'pre-wrap',
    color: '#243b53',
    fontSize: '13px',
    lineHeight: 1.55
  },
  empty: {
    color: '#9aa7b2',
    fontSize: '12px',
    fontStyle: 'italic'
  },
  choices: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  choice: {
    padding: '4px 7px',
    borderRadius: '999px',
    background: '#eaf2f8',
    color: '#315b78',
    fontSize: '11px',
    fontWeight: 700
  },
  commentButton: {
    border: '1px solid #b8c8d4',
    borderRadius: '6px',
    background: '#fff',
    color: '#315b78',
    padding: '5px 8px',
    fontSize: '10px',
    fontWeight: 800,
    cursor: 'pointer'
  },
  comment: {
    marginTop: '8px',
    padding: '8px 9px',
    borderLeft: '3px solid #5c88a6',
    borderRadius: '5px',
    background: '#f3f7fa'
  },
  commentText: {
    whiteSpace: 'pre-wrap',
    color: '#334e68',
    fontSize: '12px',
    lineHeight: 1.45
  },
  commentActions: { display: 'flex', gap: '7px', marginTop: '5px' },
  textButton: {
    border: 0,
    padding: 0,
    background: 'transparent',
    color: '#315b78',
    fontSize: '10px',
    fontWeight: 800,
    cursor: 'pointer'
  },
  deleteButton: {
    border: 0,
    padding: 0,
    background: 'transparent',
    color: '#9a2c2c',
    fontSize: '10px',
    fontWeight: 800,
    cursor: 'pointer'
  },
  addBox: { display: 'grid', gap: '6px', marginTop: '9px' },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #cbd5dc',
    borderRadius: '7px',
    padding: '8px 9px',
    resize: 'vertical',
    font: 'inherit',
    fontSize: '12px'
  },
  saveButton: {
    justifySelf: 'start',
    border: '1px solid #215b7d',
    borderRadius: '6px',
    background: '#215b7d',
    color: '#fff',
    padding: '6px 9px',
    fontSize: '10px',
    fontWeight: 800,
    cursor: 'pointer'
  }
};
